"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RUNNIT_TOP_UP_TOKENS_TEAMS_AMOUNT_MAX_QTY = exports.RUNNIT_TOP_UP_TOKENS_TEAMS_AMOUNT = exports.getRunnitTokensPackageEnterprisePrice = exports.getRunnitTokensOveragePrice = exports.getRunnitTokensPrePurchasePrice = exports.RUNNIT_TOKENS_OVERAGE_UPCHARGE_MULTIPLIER = exports.RUNNIT_TOKENS_PRE_PURCHASE_PRICE_PER_TOKEN = exports.RUNNIT_TOKENS_PACKAGE_ENTERPRISE_AMOUNT_MAX_QTY = exports.RUNNIT_TOKENS_PACKAGE_ENTERPRISE_AMOUNT_MIN_QTY = exports.RUNNIT_TOKENS_PACKAGE_ENTERPRISE_AMOUNT = exports.RUNNIT_TOKENS_PRICE_QUOTE_QTY = exports.getRunnitNodeDefCostRange = exports.getRunnitNodeRunPrice = exports.findFieldByFieldDefUuid = exports.getHardwareHourlyPrice = exports.currencyTokens = exports.calcTopUpSavingsPercent = exports.tokensToHundredths = exports.hundredthsToTokens = exports.commaSeparated = exports.currency = exports.dollarsToCents = exports.centsToDollars = void 0;
const lodash_1 = require("lodash");
const numeral = require('numeral');
const bignumber_js_1 = require("bignumber.js");
// isFieldVisibleAndEnabled / isFieldRequired moved to ./runnitNodes, next to
// getLogicOnFieldsFromField, which is the only thing they depend on. They lived
// here only because getRunnitNodeRunPrice was their first caller. Re-exported
// from the barrel either way, so no consumer changed. See PR #729.
const runnitNodes_1 = require("./runnitNodes");
const walk_1 = require("./runnitFields/walk");
const centsToDollars = (val) => {
    if (!val)
        return 0;
    const num = (0, lodash_1.toNumber)(val);
    if (!num)
        return 0;
    return (0, bignumber_js_1.default)(Math.floor(num)).div(100).toNumber(); // Cents always integer
};
exports.centsToDollars = centsToDollars;
const dollarsToCents = (val) => {
    if (!val)
        return 0;
    const num = (0, lodash_1.toNumber)(val);
    if (!num)
        return 0;
    return Math.round((0, bignumber_js_1.default)(num).times(100).toNumber()); // Cents always integer
};
exports.dollarsToCents = dollarsToCents;
const currency = (val, useNegSign = false) => {
    const ZERO_VAL = `$0.00`;
    if (!val)
        return ZERO_VAL;
    const num = (0, lodash_1.toNumber)(val);
    if (!num)
        return ZERO_VAL;
    const formatted = numeral(Math.abs(num)).format('0,0.00');
    if (num < 0 && !useNegSign)
        return `($${formatted})`;
    if (num < 0 && useNegSign)
        return `-$${formatted}`;
    return `$${formatted}`;
};
exports.currency = currency;
const commaSeparated = (val) => {
    if (!val)
        return '0';
    const num = (0, lodash_1.toNumber)(val);
    if (!num)
        return '0';
    // Convert to string and split at decimal point to count decimal places
    const MAX_DECIMALS = 10;
    const decimalPart = num.toString().split('.')[1];
    const decimalPlaces = Math.min((decimalPart || '').length || 0, MAX_DECIMALS);
    // Create dynamic format based on decimal places
    const format = decimalPlaces > 0 ? `0,0.${'0'.repeat(decimalPlaces)}` : '0,0';
    return numeral(num).format(format);
};
exports.commaSeparated = commaSeparated;
const hundredthsToTokens = (val) => {
    if (!val)
        return 0;
    const num = (0, lodash_1.toNumber)(val);
    if (!num)
        return 0;
    return Math.floor((0, bignumber_js_1.default)(Math.floor(num)).div(100).toNumber()); // Hundredths always integer
};
exports.hundredthsToTokens = hundredthsToTokens;
const tokensToHundredths = (val) => {
    if (!val)
        return 0;
    const num = (0, lodash_1.toNumber)(val);
    if (!num)
        return 0;
    return Math.round((0, bignumber_js_1.default)(num).times(100).toNumber()); // Hundredths always integer
};
exports.tokensToHundredths = tokensToHundredths;
/**
 * How much cheaper `candidate`'s top-up packs are than `baseline`'s, per token, as a whole percent —
 * 0 when they are not cheaper, or when either add-on sells no pack. The upgrade row (through
 * getRunnitTokensInfo) and the subscriptions page both quote this, so they always quote the same number.
 *
 * Cross-multiplied, not divided into per-token prices first. Price over tokens rarely terminates
 * (104 cents / 100,000 hundredths), so each per-token price is already a rounded decimal, and a ratio
 * of two rounded decimals can land at 12.4999… where the truth is 12.5: a pack that is 13% cheaper,
 * quoted as 12%. Floats did that, and so does BigNumber taking the same steps, since its division is
 * rounded too (at 20 places). Scaling both per-token prices by both token counts compares them in
 * exact integers instead, and leaves a single division of two exact integers — a true .5 is a
 * terminating decimal, so it rounds the right way every time.
 */
const calcTopUpSavingsPercent = (baseline, candidate) => {
    const baselinePrice = (0, lodash_1.get)(baseline, 'oneTimeTopUp.stripe.priceAmount') || 0;
    const baselineTokens = (0, lodash_1.get)(baseline, 'oneTimeTopUp.tokens') || 0;
    const candidatePrice = (0, lodash_1.get)(candidate, 'oneTimeTopUp.stripe.priceAmount') || 0;
    const candidateTokens = (0, lodash_1.get)(candidate, 'oneTimeTopUp.tokens') || 0;
    if (!(baselinePrice > 0) || !(baselineTokens > 0) || !(candidatePrice > 0) || !(candidateTokens > 0))
        return 0;
    // Each per-token price times baselineTokens × candidateTokens: same order, same ratio, no division
    const baselineRate = (0, bignumber_js_1.default)(baselinePrice).times(candidateTokens);
    const candidateRate = (0, bignumber_js_1.default)(candidatePrice).times(baselineTokens);
    if (candidateRate.gte(baselineRate))
        return 0;
    return baselineRate.minus(candidateRate)
        .times(100)
        .div(baselineRate)
        .integerValue(bignumber_js_1.default.ROUND_HALF_UP)
        .toNumber();
};
exports.calcTopUpSavingsPercent = calcTopUpSavingsPercent;
const currencyTokens = (val, shorthand = false, useNegSign = false) => {
    const ZERO_VAL = '0';
    if (!val)
        return ZERO_VAL;
    const num = (0, lodash_1.toNumber)(val);
    if (!num)
        return ZERO_VAL;
    const formatted = numeral(Math.abs(num)).format(shorthand ? '0[.]0a' : '0,0').toUpperCase();
    if (num < 0 && !useNegSign)
        return `(${formatted})`;
    if (num < 0 && useNegSign)
        return `-${formatted}`;
    return formatted;
};
exports.currencyTokens = currencyTokens;
const getHardwareHourlyPrice = (hardwareOffer, teamId, club) => {
    if (!hardwareOffer)
        return 0;
    const price = hardwareOffer.price || 0;
    if (teamId &&
        hardwareOffer.teamDiscount &&
        hardwareOffer.teamDiscount.percent > 0 &&
        hardwareOffer.teamDiscount.percent <= 100) {
        const priceRatio = (100 - hardwareOffer.teamDiscount.percent) / 100;
        return Math.max(Math.round(price * priceRatio), 0);
    }
    if (club &&
        hardwareOffer.clubDiscounts &&
        hardwareOffer.clubDiscounts[club] &&
        hardwareOffer.clubDiscounts[club].percent > 0 &&
        hardwareOffer.clubDiscounts[club].percent <= 100) {
        const priceRatio = (100 - hardwareOffer.clubDiscounts[club].percent) / 100;
        return Math.max(Math.round(price * priceRatio), 0);
    }
    return price;
};
exports.getHardwareHourlyPrice = getHardwareHourlyPrice;
const findFieldByFieldDefUuid = (fields, fieldDefUuid) => ((0, walk_1.eachLeafField)(fields).find((field) => field.fieldDefUuid === fieldDefUuid) || null);
exports.findFieldByFieldDefUuid = findFieldByFieldDefUuid;
const doesValueMatchRule = (value, onValue) => {
    if (onValue === true && value)
        return true;
    if ((0, lodash_1.isArray)(onValue)) {
        if (onValue.includes(value))
            return true;
        if ((0, lodash_1.isObject)(value)) {
            return onValue.some((ruleVal) => {
                if ((0, lodash_1.isObject)(ruleVal)) {
                    return Object.keys(ruleVal).every((key) => value[key] === ruleVal[key]);
                }
                return false;
            });
        }
    }
    return false;
};
const clampNonNeg = (val) => {
    if (!(0, lodash_1.isFinite)(val) || Number.isNaN(val))
        return 0;
    return Math.max(0, Math.floor(val));
};
const getRunnitNodeRunPrice = (costPerResult, numResults, costPerResultLogics, fieldValues, fields) => {
    let finalCostPerResult = costPerResult || 0;
    if (costPerResultLogics && fieldValues) {
        for (const logic of costPerResultLogics) {
            for (const [fieldDefUuid, rule] of Object.entries(logic.field)) {
                const fieldValue = fieldValues[fieldDefUuid];
                // Check if field is visible and enabled before applying pricing logic
                if (fields) {
                    const field = (0, exports.findFieldByFieldDefUuid)(fields, fieldDefUuid);
                    if (field && !(0, runnitNodes_1.isFieldVisibleAndEnabled)(field, fieldValues)) {
                        continue; // Skip pricing logic for hidden or disabled fields
                    }
                }
                let computedOperand = rule.operand;
                if (rule.operandIsFieldValue) {
                    const valueNumber = (0, lodash_1.toNumber)(fieldValue);
                    if ((0, lodash_1.isFinite)(valueNumber)) {
                        computedOperand = valueNumber;
                    }
                }
                let matchCount = 0;
                let itemsToProcess = null;
                if (rule.logicPerArrayItem) {
                    if ((0, lodash_1.isArray)(fieldValue)) {
                        itemsToProcess = fieldValue;
                    }
                    else if ((0, lodash_1.isObject)(fieldValue) && ((0, lodash_1.isArray)(fieldValue.uploads) || (0, lodash_1.isArray)(fieldValue.nodeRunResults))) {
                        itemsToProcess = [
                            ...(fieldValue.uploads || []),
                            ...(fieldValue.nodeRunResults || [])
                        ];
                    }
                }
                if (itemsToProcess !== null) {
                    for (const val of itemsToProcess) {
                        if (doesValueMatchRule(val, rule.onValue)) {
                            matchCount++;
                        }
                    }
                }
                else if (doesValueMatchRule(fieldValue, rule.onValue)) {
                    matchCount = 1;
                }
                if (matchCount > 0) {
                    for (let i = 0; i < matchCount; i++) {
                        switch (rule.operator) {
                            case 'ADD':
                                finalCostPerResult += computedOperand;
                                break;
                            case 'MULTIPLY':
                                finalCostPerResult *= computedOperand;
                                break;
                            case 'SET':
                                finalCostPerResult = computedOperand;
                                break;
                            default:
                                break;
                        }
                    }
                }
            }
        }
    }
    return (0, bignumber_js_1.default)(finalCostPerResult).times(numResults || 1).integerValue(bignumber_js_1.default.ROUND_FLOOR).toNumber();
};
exports.getRunnitNodeRunPrice = getRunnitNodeRunPrice;
const getFieldCandidateValues = (field) => {
    if (!field)
        return [null, true];
    const display = field.display;
    switch (field.type) {
        case 'SINGLE_SELECT':
        case 'MULTI_SELECT': {
            const opts = (display && display.text && display.text.options) || (display && display.radio && display.radio.options) || (display && display.image && display.image.options) || [];
            const vals = opts.map((o) => o.value);
            return vals.length ? vals : [null];
        }
        case 'WIDTH_HEIGHT': {
            if (display && display.select && display.select.options && display.select.options.length) {
                return display.select.options.map((o) => o.value);
            }
            return [null];
        }
        case 'BOOLEAN':
            return [true, false];
        case 'SLIDER':
        case 'UPSCALE_FACTOR_SLIDER':
        case 'CFG':
        case 'STEPS':
        case 'NUM': {
            const vals = [];
            if (display && (0, lodash_1.isFinite)(display.min))
                vals.push(display.min);
            if (display && (0, lodash_1.isFinite)(display.max))
                vals.push(display.max);
            return vals.length ? vals : [null, 1];
        }
        case 'IMGS': {
            const maxImages = (display && (0, lodash_1.isFinite)(display.maxImages) && display.maxImages > 0) ? display.maxImages : 4;
            const counts = [0];
            for (let i = 1; i <= maxImages; i++)
                counts.push(i);
            return counts;
        }
        default:
            return [null, true];
    }
};
/**
 * Computes the theoretical min/max cost-per-result for display on offering cards.
 * Uses a cross-product approach: enumerates candidate values for each field referenced
 * by costPerResultLogics, simulates the full logic chain for every combination, and
 * returns the observed min/max. This correctly handles mutually-exclusive rules on
 * the same field and operandIsFieldValue.
 *
 * This is NOT used for actual billing — see getRunnitNodeRunPrice for that.
 */
const getRunnitNodeDefCostRange = (costPerResult, costPerResultLogics, fields) => {
    const baseCost = clampNonNeg(costPerResult || 0);
    if (!costPerResultLogics || !costPerResultLogics.length || !fields || !fields.length) {
        return { minCostPerResult: baseCost, maxCostPerResult: baseCost };
    }
    const fieldMap = {};
    const walkFields = (list) => {
        for (const field of (0, walk_1.eachLeafField)(list)) {
            fieldMap[field.fieldDefUuid] = field;
        }
    };
    walkFields(fields);
    const refUuids = [];
    const hasArrayLogic = {};
    const seen = {};
    for (const logic of costPerResultLogics) {
        for (const [uuid, rule] of Object.entries(logic.field)) {
            if (!seen[uuid]) {
                seen[uuid] = true;
                refUuids.push(uuid);
            }
            if (rule.logicPerArrayItem)
                hasArrayLogic[uuid] = true;
        }
    }
    const allCandidates = [];
    for (const uuid of refUuids) {
        const field = fieldMap[uuid];
        if (hasArrayLogic[uuid]) {
            const display = field && field.display;
            let maxItems = 4;
            if (field && field.type === 'IMGS' && display && (0, lodash_1.isFinite)(display.maxImages) && display.maxImages > 0) {
                maxItems = display.maxImages;
            }
            else if (field && field.type === 'MULTI_SELECT') {
                const opts = (display && display.text && display.text.options) || (display && display.radio && display.radio.options) || (display && display.image && display.image.options) || [];
                if (opts.length)
                    maxItems = opts.length;
            }
            allCandidates.push([0, maxItems]);
        }
        else {
            allCandidates.push(getFieldCandidateValues(field));
        }
    }
    let productSize = 1;
    for (const c of allCandidates) {
        productSize *= c.length;
        if (productSize > 50000) {
            return { minCostPerResult: baseCost, maxCostPerResult: baseCost };
        }
    }
    let minCost = Infinity;
    let maxCost = -Infinity;
    const evaluate = (fieldIdx, fieldVals) => {
        if (fieldIdx === refUuids.length) {
            let cost = baseCost;
            for (const logic of costPerResultLogics) {
                for (const [uuid, rule] of Object.entries(logic.field)) {
                    const isArray = !!hasArrayLogic[uuid];
                    const val = fieldVals[uuid];
                    let matchCount = 0;
                    if (isArray) {
                        matchCount = (typeof val === 'number') ? val : 0;
                    }
                    else {
                        if (val != null && doesValueMatchRule(val, rule.onValue)) {
                            matchCount = 1;
                        }
                    }
                    if (matchCount <= 0)
                        continue;
                    let operand = (0, lodash_1.isFinite)(rule.operand) ? rule.operand : 0;
                    if (rule.operandIsFieldValue && !isArray) {
                        const n = (0, lodash_1.toNumber)(val);
                        if ((0, lodash_1.isFinite)(n))
                            operand = n;
                    }
                    for (let i = 0; i < matchCount; i++) {
                        switch (rule.operator) {
                            case 'ADD':
                                cost += operand;
                                break;
                            case 'MULTIPLY':
                                cost *= operand;
                                break;
                            case 'SET':
                                cost = operand;
                                break;
                        }
                    }
                }
            }
            cost = clampNonNeg(cost);
            if (cost < minCost)
                minCost = cost;
            if (cost > maxCost)
                maxCost = cost;
            return;
        }
        for (const v of allCandidates[fieldIdx]) {
            fieldVals[refUuids[fieldIdx]] = v;
            evaluate(fieldIdx + 1, fieldVals);
        }
    };
    evaluate(0, {});
    return {
        minCostPerResult: minCost === Infinity ? baseCost : minCost,
        maxCostPerResult: maxCost === -Infinity ? baseCost : maxCost,
    };
};
exports.getRunnitNodeDefCostRange = getRunnitNodeDefCostRange;
/*
Pre-purchase Tokens on Enterprise plans

A single token costs a fraction of a cent, so prices are quoted per RUNNIT_TOKENS_PRICE_QUOTE_QTY
tokens. Every price below is in CENTS — callers stay in cents and convert at render time, with
the centsToDollars filter. Rates may carry fractional cents; only whole amounts are rounded.
 */
exports.RUNNIT_TOKENS_PRICE_QUOTE_QTY = 1000;
exports.RUNNIT_TOKENS_PACKAGE_ENTERPRISE_AMOUNT = (0, exports.tokensToHundredths)(500000);
exports.RUNNIT_TOKENS_PACKAGE_ENTERPRISE_AMOUNT_MIN_QTY = 3;
exports.RUNNIT_TOKENS_PACKAGE_ENTERPRISE_AMOUNT_MAX_QTY = 1000;
// Default pre-purchase price, in dollars per token, matching Company.prePurchaseRunnitTokensPricePerToken
exports.RUNNIT_TOKENS_PRE_PURCHASE_PRICE_PER_TOKEN = 0.0006;
// Month-to-month overage carries a 25% upcharge over the pre-purchase price, at every price point
exports.RUNNIT_TOKENS_OVERAGE_UPCHARGE_MULTIPLIER = 1.25;
/*
Pre-purchase price, in cents per RUNNIT_TOKENS_PRICE_QUOTE_QTY tokens, for a Company: its
negotiated rate, else the default. Rates are quoted in dollars per token, which is how deals are
struck and how the Company doc stores them, so this is the one place the two units meet.
 */
const getRunnitTokensPrePurchasePrice = (company) => (0, exports.dollarsToCents)((0, bignumber_js_1.default)((company && company.prePurchaseRunnitTokensPricePerToken) || exports.RUNNIT_TOKENS_PRE_PURCHASE_PRICE_PER_TOKEN)
    .times(exports.RUNNIT_TOKENS_PRICE_QUOTE_QTY)
    .toNumber());
exports.getRunnitTokensPrePurchasePrice = getRunnitTokensPrePurchasePrice;
// Month-to-month overage price, in cents per RUNNIT_TOKENS_PRICE_QUOTE_QTY tokens, for a Company
const getRunnitTokensOveragePrice = (company) => ((0, bignumber_js_1.default)((0, exports.getRunnitTokensPrePurchasePrice)(company))
    .times(exports.RUNNIT_TOKENS_OVERAGE_UPCHARGE_MULTIPLIER)
    .toNumber());
exports.getRunnitTokensOveragePrice = getRunnitTokensOveragePrice;
// Price, in cents, of a single RUNNIT_TOKENS_PACKAGE_ENTERPRISE_AMOUNT pre-purchase package for a Company
const getRunnitTokensPackageEnterprisePrice = (company) => Math.round((0, bignumber_js_1.default)((0, exports.getRunnitTokensPrePurchasePrice)(company))
    .times((0, exports.hundredthsToTokens)(exports.RUNNIT_TOKENS_PACKAGE_ENTERPRISE_AMOUNT))
    .div(exports.RUNNIT_TOKENS_PRICE_QUOTE_QTY)
    .toNumber());
exports.getRunnitTokensPackageEnterprisePrice = getRunnitTokensPackageEnterprisePrice;
/*
Top up tokens amount: The amount of tokens you can buy if you're low, but don't want to upgrade yet
 */
exports.RUNNIT_TOP_UP_TOKENS_TEAMS_AMOUNT = (0, exports.tokensToHundredths)(10000);
exports.RUNNIT_TOP_UP_TOKENS_TEAMS_AMOUNT_MAX_QTY = 50;
